import React from 'react';
import { CourseModule } from '../../types/course';
import {
  DocumentTextIcon,
  PhotoIcon,
  VideoCameraIcon,
  DocumentIcon,
  CodeBracketIcon,
  ArchiveBoxIcon,
  LinkIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import {
  RichTextView,
  ImageView,
  VideoView,
  PdfView,
  ScormView,
  LinkView,
  getEvalViewEntry,
  getFeedbackViewEntry,
} from '../modules/views/registry';

interface ModulePreviewProps {
  module: CourseModule;
  'data-testid'?: string;
}

const ICON_CLASS = 'w-5 h-5';
const MODULE_TYPE_ICONS: Record<string, React.ReactElement> = {
  rich_text: <DocumentTextIcon className={ICON_CLASS} />,
  image_embed: <PhotoIcon className={ICON_CLASS} />,
  video_url_embed: <VideoCameraIcon className={ICON_CLASS} />,
  pdf_file: <DocumentIcon className={ICON_CLASS} />,
  source_code_file: <CodeBracketIcon className={ICON_CLASS} />,
  scorm_zip: <ArchiveBoxIcon className={ICON_CLASS} />,
  video_meeting_link: <LinkIcon className={ICON_CLASS} />,
  evaluation: <CheckCircleIcon className={ICON_CLASS} />,
  feedback: <ChatBubbleLeftRightIcon className={ICON_CLASS} />,
};

const MODULE_TYPE_NAMES: Record<string, string> = {
  rich_text: 'Rich Text',
  image_embed: 'Image',
  video_url_embed: 'Video',
  pdf_file: 'PDF Document',
  source_code_file: 'Code File',
  scorm_zip: 'SCORM Package',
  video_meeting_link: 'Embedded link',
  evaluation: 'Quiz',
  feedback: 'Survey',
};

const ModulePreview: React.FC<ModulePreviewProps> = ({
  module,
  'data-testid': testId = 'module-preview',
}) => {
  const content = module.details ?? {};

  const renderContent = () => {
    switch (module.type) {
      case 'rich_text':
        return <RichTextView item={{ content: content.content as string }} mode="preview" />;

      case 'image_embed':
        return (
          <ImageView
            item={{
              image_url: content.image_url as string,
              alt_text: content.alt_text as string,
              caption: content.caption as string,
            }}
            mode="preview"
          />
        );

      case 'video_url_embed':
        return (
          <VideoView
            item={{ video_url: content.video_url as string, transcript: content.transcript as string }}
            mode="preview"
          />
        );

      case 'pdf_file':
        return (
          <PdfView
            item={{
              file_url: content.file_url as string,
              filename: content.filename as string,
              size_bytes: content.size_bytes as number,
            }}
            mode="preview"
          />
        );

      case 'source_code_file':
        return (
          <div className="space-y-2">
            {content.language ? (
              <span className="inline-block px-2 py-1 bg-surface-secondary text-xs font-mono rounded border border-border">
                {String(content.language)}
              </span>
            ) : null}
            <pre className="p-4 bg-grey-900 text-grey-100 rounded-lg overflow-x-auto text-sm font-mono">
              <code>{String(content.code || '// No code provided')}</code>
            </pre>
          </div>
        );

      case 'scorm_zip':
        return (
          <ScormView
            item={{ file_url: content.file_url as string, module_id: module.id }}
            mode="preview"
          />
        );

      case 'video_meeting_link':
        return (
          <LinkView
            item={{
              meeting_url: content.meeting_url as string,
              text: content.text as string,
              title: module.title,
            }}
            mode="preview"
          />
        );

      case 'evaluation': {
        const items = Array.isArray(content.items) ? content.items : [];
        const passing = content.passing_grade_percent as number | undefined;
        return (
          <div className="flex flex-col gap-6">
            {typeof passing === 'number' && (
              <p className="text-sm text-text-secondary">
                Passing grade:{' '}
                <span className="font-semibold text-text-primary">{passing}%</span>
              </p>
            )}
            {items.length === 0 ? (
              <p className="text-sm text-text-secondary italic">No questions yet.</p>
            ) : (
              items
                .slice()
                .sort((a: { order_index: number }, b: { order_index: number }) => a.order_index - b.order_index)
                .map((it: { id: string; type: string; order_index: number }) => {
                  const { View } = getEvalViewEntry(it.type as Parameters<typeof getEvalViewEntry>[0]);
                  return (
                    <div key={it.id} className="p-4 rounded-lg border border-border bg-surface-primary">
                      <View item={it} mode="preview" disabled />
                    </div>
                  );
                })
            )}
          </div>
        );
      }

      case 'feedback': {
        const items = Array.isArray(content.items) ? content.items : [];
        return (
          <div className="flex flex-col gap-6">
            {content.anonymous ? (
              <p className="text-sm text-text-secondary italic">Responses collected anonymously.</p>
            ) : null}
            {items.length === 0 ? (
              <p className="text-sm text-text-secondary italic">No questions yet.</p>
            ) : (
              items
                .slice()
                .sort((a: { order_index: number }, b: { order_index: number }) => a.order_index - b.order_index)
                .map((it: { id: string; type: string; order_index: number }) => {
                  const { View } = getFeedbackViewEntry(it.type as Parameters<typeof getFeedbackViewEntry>[0]);
                  return (
                    <div key={it.id} className="p-4 rounded-lg border border-border bg-surface-primary">
                      <View item={it} mode="preview" disabled />
                    </div>
                  );
                })
            )}
          </div>
        );
      }

      default:
        return (
          <div className="p-6 bg-surface-secondary rounded-lg border border-border text-center">
            <p className="text-text-secondary">Preview not available for this module type</p>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4" data-testid={`${testId}-${module.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 text-primary-600 dark:text-primary-400">
          {MODULE_TYPE_ICONS[module.type] ?? <DocumentTextIcon className={ICON_CLASS} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className="text-lg font-semibold text-text-primary"
              data-testid={`${testId}-title-${module.id}`}
            >
              {module.title}
            </h3>
            <span className="px-2 py-0.5 bg-text-tertiary/10 text-text-secondary text-xs rounded">
              {MODULE_TYPE_NAMES[module.type] ?? 'Module'}
            </span>
          </div>
          {module.subtitle && (
            <p
              className="text-sm text-text-secondary"
              data-testid={`${testId}-description-${module.id}`}
            >
              {module.subtitle}
            </p>
          )}
        </div>
      </div>
      <div data-testid={`${testId}-content-${module.id}`}>{renderContent()}</div>
    </div>
  );
};

export default ModulePreview;
